import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Plus, Trash2, Check, Download, Edit3 } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getWeddings, getBudget, addBudgetItem, updateBudgetItem, deleteBudgetItem, exportBudgetExcel, updateWeddingCurrency } from '../services/api';

function BudgetTracker() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [, setSummary] = useState({});
  const [weddingId, setWeddingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('NPR');
  const [showAdd, setShowAdd] = useState(false);
  const emptyItem = { category: 'Venue', item_name: '', estimated_cost_npr: '', actual_cost_npr: '', is_paid: false, notes: '' };
  const [newItem, setNewItem] = useState(emptyItem);
  // Items used to be add-only, so an expense could never be marked paid or get its actual cost later
  const [editingItem, setEditingItem] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // Only couples and delegates can access budget
    if (user.role === 'helper' && user.collaborator?.collaborator_role !== 'delegate') {
      navigate('/checklist');
      return;
    }
    setLoadError(false);
    getWeddings()
      .then((res) => {
        if (res.data.length > 0) {
          setWeddingId(res.data[0].id);
          setIsOwner(res.data[0].user_id === user.id);
          setCurrency(res.data[0].budget_currency || 'NPR');
          return getBudget(res.data[0].id);
        }
      })
      .then((res) => {
        if (res) {
          setItems(res.data.items);
          setSummary(res.data.summary);
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [user, navigate, authLoading, reloadKey]);

  // Same rule as routes/budget.js: read-only delegates can look but not change anything
  const canWrite = isOwner || user?.collaborator?.access_level === 'write';

  const closeForm = () => { setShowAdd(false); setEditingItem(null); setNewItem(emptyItem); };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingItem) {
        const res = await updateBudgetItem(editingItem.id, {
          ...newItem,
          vendor_id: editingItem.vendor_id || null,
          actual_cost_npr: parseFloat(newItem.actual_cost_npr) || 0,
        });
        setItems(items.map(i => i.id === editingItem.id ? { ...i, ...res.data } : i));
      } else {
        const res = await addBudgetItem(weddingId, newItem);
        setItems([...items, res.data]);
      }
      closeForm();
    } catch (err) {
      setError(err.response?.data?.error || t('coupleTools.actionFailed'));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setNewItem({
      category: item.category || 'Venue', item_name: item.item_name || '',
      estimated_cost_npr: item.estimated_cost_npr ?? '', actual_cost_npr: item.actual_cost_npr ?? '',
      is_paid: !!item.is_paid, notes: item.notes || '',
    });
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      await deleteBudgetItem(id);
      setItems(items.filter(i => i.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || t('coupleTools.actionFailed'));
    }
  };

  const handleCurrencyChange = async (newCurrency) => {
    if (newCurrency === currency || !weddingId) return;
    const previous = currency;
    setCurrency(newCurrency);
    setError('');
    try {
      await updateWeddingCurrency(weddingId, newCurrency);
    } catch (err) {
      setCurrency(previous);
      setError(err.response?.data?.error || t('coupleTools.actionFailed'));
    }
  };

  const currencySymbols = { NPR: 'रू', USD: '$', INR: '₹', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$', JPY: '¥', AED: 'د.إ', QAR: 'ر.ق' };
  const currencySymbol = currencySymbols[currency] || currency;

  const totalEstimated = items.reduce((sum, i) => sum + parseFloat(i.estimated_cost_npr || 0), 0);
  const totalActual = items.reduce((sum, i) => sum + parseFloat(i.actual_cost_npr || 0), 0);
  const totalPaid = items.filter(i => i.is_paid).reduce((sum, i) => sum + parseFloat(i.actual_cost_npr || 0), 0);
  const remaining = totalEstimated - totalActual;

  // Group by category
  const categorized = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  // Without a wedding the old page showed zeros and an Add button that posted to /budget/null
  if (loadError || !weddingId) {
    return (
      <div className="budget-page">
        <div className="container">
          <div className="empty-state-sm">
            <Wallet size={40} />
            <h2>{loadError ? t('coupleTools.loadFailed') : t('coupleTools.noWeddingTitle')}</h2>
            {!loadError && <p>{user?.role === 'couple' ? t('coupleTools.noWeddingDesc') : t('coupleTools.noTeamDesc')}</p>}
            <div style={{ marginTop: 16 }}>
              {loadError ? (
                <button className="btn btn-primary" onClick={() => { setLoading(true); setReloadKey(k => k + 1); }}>{t('coupleTools.tryAgain')}</button>
              ) : user?.role === 'couple' && (
                <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>{t('coupleTools.goToDashboard')}</button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="budget-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1><Wallet size={28} /> {t('budget.title')}</h1>
            <p>{t('budget.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Currency select */}
            {/* only the couple may change the currency (PATCH /weddings/:id/currency) */}
            <select
              value={currency}
              disabled={!isOwner}
              title={isOwner ? undefined : t('coupleTools.ownerOnly')}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              style={{ padding: '7px 14px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: '#374151', background: '#fff', outline: 'none' }}
            >
              <option value="NPR">NPR रू</option>
              <option value="USD">USD $</option>
              <option value="INR">INR ₹</option>
              <option value="EUR">EUR €</option>
              <option value="GBP">GBP £</option>
              <option value="AUD">AUD A$</option>
              <option value="CAD">CAD C$</option>
              <option value="JPY">JPY ¥</option>
              <option value="AED">AED د.إ</option>
              <option value="QAR">QAR ر.ق</option>
            </select>
            {weddingId && items.length > 0 && (
              <button className="btn btn-outline" onClick={async () => {
                try {
                  const res = await exportBudgetExcel(weddingId);
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'NepWed_Budget.xlsx');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (err) { alert(t('budget.failedExport')); }
              }}>
                <Download size={16} /> {t('budget.exportExcel')}
              </button>
            )}
            {canWrite && (
              <button className="btn btn-primary" onClick={() => (showAdd ? closeForm() : setShowAdd(true))}>
                <Plus size={16} /> {t('budget.addExpense')}
              </button>
            )}
          </div>
        </div>

        {!canWrite && <div className="alert alert-warning">{t('coupleTools.readOnlyNotice')}</div>}
        {error && (
          <div className="alert alert-error">
            {error}
            <button className="alert-close" onClick={() => setError('')}>&times;</button>
          </div>
        )}

        {/* Budget Summary */}
        <div className="budget-summary">
          <div className="budget-card total">
            <span className="budget-label">{t('budget.totalBudget')}</span>
            <span className="budget-value">{currencySymbol} {totalEstimated.toLocaleString()}</span>
          </div>
          <div className="budget-card spent">
            <span className="budget-label">{t('budget.totalSpent')}</span>
            <span className="budget-value">{currencySymbol} {totalActual.toLocaleString()}</span>
          </div>
          <div className="budget-card paid">
            <span className="budget-label">{t('budget.totalPaid')}</span>
            <span className="budget-value">{currencySymbol} {totalPaid.toLocaleString()}</span>
          </div>
          <div className="budget-card remaining" style={{ color: remaining >= 0 ? '#22C55E' : '#EF4444' }}>
            <span className="budget-label">{t('budget.remaining')}</span>
            <span className="budget-value">{currencySymbol} {remaining.toLocaleString()}</span>
          </div>
        </div>

        {/* Budget Progress */}
        <div className="budget-progress-section">
          <div className="progress-header">
            <span>{t('budget.budgetUsed')} {totalEstimated ? Math.round((totalActual / totalEstimated) * 100) : 0}%</span>
          </div>
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{
                width: `${totalEstimated ? Math.min((totalActual / totalEstimated) * 100, 100) : 0}%`,
                background: totalActual > totalEstimated ? '#EF4444' : '#DC143C'
              }}
            ></div>
          </div>
        </div>

        {/* Add Item Form */}
        {showAdd && (
          <form className="add-form" onSubmit={handleAdd}>
            <div className="form-row">
              <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}>
                <option value="Venue">{t('budget.venue')}</option>
                <option value="Catering">{t('budget.catering')}</option>
                <option value="Photography">{t('budget.photography')}</option>
                <option value="Decoration">{t('budget.decoration')}</option>
                <option value="Bridal Attire">{t('budget.bridalAttire')}</option>
                <option value="Groom Attire">{t('budget.groomAttire')}</option>
                <option value="Mehendi">{t('budget.mehendi')}</option>
                <option value="Music">{t('budget.music')}</option>
                <option value="Makeup">{t('budget.makeup')}</option>
                <option value="Priest">{t('budget.priest')}</option>
                <option value="Invitations">{t('budget.invitations')}</option>
                <option value="Transportation">{t('budget.transportation')}</option>
                <option value="Jewellery">{t('budget.jewellery')}</option>
                <option value="Miscellaneous">{t('budget.miscellaneous')}</option>
              </select>
              <input type="text" placeholder={t('budget.itemName')} value={newItem.item_name} onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })} required />
            </div>
            <div className="form-row">
              <input type="number" placeholder={t('budget.estimatedCost')} value={newItem.estimated_cost_npr} onChange={(e) => setNewItem({ ...newItem, estimated_cost_npr: e.target.value })} required />
              <input type="number" placeholder={t('budget.actualCost')} value={newItem.actual_cost_npr} onChange={(e) => setNewItem({ ...newItem, actual_cost_npr: e.target.value })} />
            </div>
            <input type="text" placeholder={t('budget.notes')} value={newItem.notes} onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })} />
            <label className="checkbox-inline">
              <input type="checkbox" checked={newItem.is_paid} onChange={(e) => setNewItem({ ...newItem, is_paid: e.target.checked })} />
              {t('budget.alreadyPaid')}
            </label>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">{editingItem ? t('budget.saveItem') : t('budget.addItem')}</button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>{t('common.cancel')}</button>
            </div>
          </form>
        )}

        {/* Budget Items by Category */}
        <div className="budget-categories">
          {items.length === 0 && !showAdd && (
            <div className="empty-state-sm"><p>{canWrite ? t('budget.emptyCanAdd') : t('budget.empty')}</p></div>
          )}
          {Object.entries(categorized).map(([category, categoryItems]) => {
            const catTotal = categoryItems.reduce((sum, i) => sum + parseFloat(i.estimated_cost_npr || 0), 0);
            return (
              <div key={category} className="budget-category">
                <div className="category-header">
                  <h3>{category}</h3>
                  <span className="category-total">{currencySymbol} {catTotal.toLocaleString()}</span>
                </div>
                <div className="budget-items">
                  {categoryItems.map((item) => (
                    <div key={item.id} className={`budget-item ${item.is_paid ? 'paid' : ''}`}>
                      <div className="item-info">
                        <h4>{item.item_name}</h4>
                        {item.vendor_name && <span className="vendor-tag">{item.vendor_name}</span>}
                        {item.notes && <p className="item-notes">{item.notes}</p>}
                      </div>
                      <div className="item-amounts">
                        <div className="amount-row">
                          <span>{t('budget.estimated')}</span>
                          <strong>{currencySymbol} {Number(item.estimated_cost_npr).toLocaleString()}</strong>
                        </div>
                        <div className="amount-row">
                          <span>{t('budget.actual')}</span>
                          <strong>{currencySymbol} {Number(item.actual_cost_npr).toLocaleString()}</strong>
                        </div>
                        {item.is_paid && <span className="paid-badge"><Check size={12} /> {t('budget.paid')}</span>}
                      </div>
                      {canWrite && (
                        <>
                          <button className="delete-btn" title={t('common.edit')} onClick={() => handleEdit(item)}>
                            <Edit3 size={14} />
                          </button>
                          <button className="delete-btn" title={t('common.delete')} onClick={() => handleDelete(item.id)}>
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default BudgetTracker;
